import { useRef, useState } from 'react';
import { Download, X } from 'lucide-react';
import { TextField } from '../../components/ui/TextField';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Sheet } from '../../components/ui/Sheet';
import {
  parseImportMessagesCSV,
  importMessagesCSV,
  type CSVImportResult,
  type CSVMessageMapping,
  type CSVFormatOptions,
  type SkippedCSVRow,
} from '../../api/dataApi';
import { queryClient } from '../../lib/queryClient';
import { DELIMITER_OPTIONS, resolveDelimiterParam, downloadSkippedRowsReport, type DelimiterChoice } from '../../lib/csvFormat';
import type { TemplateSignal } from '../../types';

// A small, persistent (manually-dismissed - NOT the standard auto-dismiss
// Toast, since "download this" shouldn't vanish after 3s) banner offering
// the "downloadable error report for bad rows" the CSV import roadmap item
// calls for. Fixed-position like Toast (so it drops into either caller's
// render tree without disturbing in-flow layout), stacked below it
// (top-20 vs Toast's top-4) so a success Toast and this banner can be
// visible at once without overlapping. Shared by both of
// CsvMessageImportSheet's callers (MessagesScreen.tsx's compose-bar "+"
// menu, ChannelManageScreen.tsx's Messages section) rather than each
// building its own - onImported already hands both an identical
// CSVImportResult.
export function CsvImportErrorBanner({ rows, onDismiss }: { rows: SkippedCSVRow[]; onDismiss: () => void }) {
  if (rows.length === 0) return null;
  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-[min(420px,calc(100vw-2rem))]">
      <div className="rounded-xl bg-amber-500 text-white shadow-lg px-4 py-3 flex items-center gap-2.5">
        <span className="flex-1 text-sm">
          {rows.length} row{rows.length === 1 ? '' : 's'} skipped.
        </span>
        <button type="button" onClick={() => downloadSkippedRowsReport(rows)} className="inline-flex items-center gap-1 text-sm font-medium hover:underline shrink-0">
          <Download size={14} />
          Download report
        </button>
        <button type="button" onClick={onDismiss} className="shrink-0 opacity-80 hover:opacity-100">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

const CSV_MESSAGE_FIELDS = [
  { key: 'text', label: 'Text', required: true },
  { key: 'sender', label: 'Sender', required: false },
  { key: 'date', label: 'Date', required: false },
  { key: 'type', label: 'Type', required: false },
  { key: 'value', label: 'Value', required: false },
] as const;
type CSVMessageFieldKey = (typeof CSV_MESSAGE_FIELDS)[number]['key'];

const CSV_MESSAGE_FIELD_KEYWORDS: Record<CSVMessageFieldKey, string[]> = {
  text: ['text', 'message', 'body', 'content'],
  sender: ['sender', 'from', 'author', 'user'],
  date: ['date', 'time', 'timestamp'],
  type: ['type', 'signal'],
  value: ['value', 'amount'],
};

// Pick a CSV file, say whether its first row is a header, map each target
// message field to a source column (best-guessed from header text when
// present, always editable), preview the result, then bulk-import as
// messages into this channel. Reachable from two places - the channel
// manage screen (settings) and the compose-bar "+" menu - so it lives in
// its own file. Gated client-side by the caller (owner-only in manage, an
// `allowImportCsv` permission from the compose bar) and, for real,
// server-side (see backend/modules/signal/transitions/signal.go's
// ImportMessagesCSV). CSV parsing and the actual per-row writes both
// happen server-side, shared with the iOS app's equivalent flow, rather
// than looping one POST /signal per row from the client (WSL has no loop
// construct to do that bulk write any other way - see ImportMessagesCSV's
// header comment).
const CSV_TYPE_MAP_CUSTOM = '__custom__';

export function CsvMessageImportSheet({
  open,
  onClose,
  channelNickname,
  availableSignals,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  channelNickname: string;
  // The channel's actually-attached templates' signals - offered as
  // dropdown targets in the value-mapping list below, so "server467 ->
  // Signal0" picks a real signal (matching its emoji/color everywhere
  // else) instead of a free-text guess at its name.
  availableSignals: TemplateSignal[];
  onImported: (result: CSVImportResult) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [csvText, setCsvText] = useState('');
  const [rows, setRows] = useState<string[][]>([]);
  const [hasHeaderRow, setHasHeaderRow] = useState(true);
  const [mapping, setMapping] = useState<Partial<Record<CSVMessageFieldKey, number>>>({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  // Auto-detect (the backend's own delimiter sniffing) is the default -
  // these two only matter once someone overrides them because
  // auto-detection guessed wrong for their file.
  const [delimiterChoice, setDelimiterChoice] = useState<DelimiterChoice>('auto');
  const [customDelimiter, setCustomDelimiter] = useState('');
  const [quoted, setQuoted] = useState(true);
  // Raw Type-column value -> desired type (e.g. a signal name) - only
  // entries someone actually edits away from identity end up here; a
  // value with no entry just imports as-is. Keyed by the raw value itself
  // rather than column index, so it survives switching which column is
  // mapped to Type without needing an explicit reset.
  const [typeValueMap, setTypeValueMap] = useState<Record<string, string>>({});

  const currentFormat = (): CSVFormatOptions => ({
    delimiter: resolveDelimiterParam(delimiterChoice, customDelimiter),
    quoted,
  });

  const headerRow = hasHeaderRow ? rows[0] : undefined;
  const dataRows = hasHeaderRow ? rows.slice(1) : rows;
  const columnCount = rows.reduce((max, r) => Math.max(max, r.length), 0);

  const columnLabel = (i: number) => headerRow?.[i]?.trim() || `Column ${i + 1}`;

  const fieldValue = (row: string[], key: CSVMessageFieldKey): string | undefined => {
    const idx = mapping[key];
    if (idx === undefined || idx >= row.length) return undefined;
    const v = row[idx].trim();
    return v === '' ? undefined : v;
  };

  // Local re-derivation purely for the live preview list, same rationale
  // as TemplateManageScreen's CSVSignalImportSheet - the actual Import
  // always re-derives the final result from the backend, not this.
  const previewRows = dataRows.filter((row) => fieldValue(row, 'text') !== undefined);

  // Every distinct value actually present in whichever column is mapped
  // to Type - the raw material for the value-mapping list below. Derived
  // fresh from mapping/dataRows rather than cached, so switching which
  // column is mapped to Type (or re-parsing with a different format)
  // never shows stale values.
  const distinctTypeValues = (() => {
    const idx = mapping.type;
    if (idx === undefined) return [];
    const seen = new Set<string>();
    for (const row of dataRows) {
      const v = row[idx]?.trim();
      if (v) seen.add(v);
    }
    return Array.from(seen).sort();
  })();

  // Only the entries that actually remap something (identity/blank ones
  // are the same as omitting the row from the table entirely) - and only
  // for values that still exist, so a stale edit from a since-changed
  // mapping never leaks into what's sent.
  const typeValueMapParam = (): string | undefined => {
    const entries = distinctTypeValues
      .map((v): [string, string] => [v, (typeValueMap[v] ?? '').trim()])
      .filter(([v, target]) => target !== '' && target !== v);
    return entries.length > 0 ? JSON.stringify(Object.fromEntries(entries)) : undefined;
  };

  // Deduped by name (several templates can define a signal with the same
  // name - the dropdown only needs to offer each once) for the value-
  // mapping selects below.
  const dedupedSignals = (() => {
    const seen = new Map<string, TemplateSignal>();
    for (const s of availableSignals) if (!seen.has(s.name)) seen.set(s.name, s);
    return Array.from(seen.values());
  })();
  const signalNames = new Set(dedupedSignals.map((s) => s.name));

  // What the dropdown for a given raw value should show as selected: ""
  // (leave as-is), the signal name if the current mapping target is one
  // of dedupedSignals, or the "Custom..." sentinel if it's some other
  // non-empty string (revealing the free-text field below the select).
  const typeSelectValue = (v: string): string => {
    const target = typeValueMap[v] ?? '';
    if (target === '') return '';
    return signalNames.has(target) ? target : CSV_TYPE_MAP_CUSTOM;
  };

  // Best-effort guess at which column is which, based on header text -
  // still fully editable via the selects below.
  const autoMapColumns = (header: string[] | undefined) => {
    const next: Partial<Record<CSVMessageFieldKey, number>> = {};
    if (header) {
      for (const field of CSV_MESSAGE_FIELDS) {
        const idx = header.findIndex((h) => {
          const normalized = h.toLowerCase().replace(/\s+/g, '');
          return CSV_MESSAGE_FIELD_KEYWORDS[field.key].some((k) => normalized.includes(k));
        });
        if (idx !== -1) next[field.key] = idx;
      }
    }
    setMapping(next);
  };

  // Shared by the initial file pick and by re-parsing after someone
  // overrides the delimiter/quoted controls below - takes `text`/
  // `format` explicitly rather than reading state, since a just-called
  // setCsvText/setDelimiterChoice hasn't landed yet when this runs.
  const parseAndSetRows = async (text: string, format: CSVFormatOptions) => {
    setLoading(true);
    setError('');
    try {
      // See TemplateManageScreen.tsx's identical parseSignalsCSV comment.
      const parsedRows = await queryClient.query({
        queryKey: ['csvParseMessages', text, format.delimiter, format.quoted],
        queryFn: () => parseImportMessagesCSV(text, format),
      });
      if (parsedRows.length === 0) {
        setError("That file doesn't contain any rows.");
        setRows([]);
        return;
      }
      setRows(parsedRows);
      autoMapColumns(hasHeaderRow ? parsedRows[0] : undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to read that file.');
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (file: File) => {
    const text = await file.text();
    setCsvText(text);
    await parseAndSetRows(text, currentFormat());
  };

  // Re-parses the already-loaded file against a corrected delimiter/
  // quoted choice - a no-op before any file is picked.
  const handleFormatChange = (format: CSVFormatOptions) => {
    if (!csvText) return;
    void parseAndSetRows(csvText, format);
  };

  const columnParam = (key: CSVMessageFieldKey) => (mapping[key] !== undefined ? String(mapping[key]) : '');

  const commitImport = async () => {
    setImporting(true);
    setError('');
    try {
      const mappingParam: CSVMessageMapping = {
        sender: columnParam('sender'),
        text: columnParam('text'),
        date: columnParam('date'),
        type: columnParam('type'),
        value: columnParam('value'),
      };
      // Write, not a read - retry: 0, same rationale as
      // TemplateManageScreen.tsx's identical importSignalsCSV comment.
      const format = currentFormat();
      const typeValueMapArg = typeValueMapParam();
      const result = await queryClient.query({
        queryKey: ['csvImportMessages', channelNickname, csvText, hasHeaderRow, mappingParam, format.delimiter, format.quoted, typeValueMapArg],
        queryFn: () => importMessagesCSV(channelNickname, csvText, hasHeaderRow, mappingParam, format, typeValueMapArg),
        retry: 0,
        staleTime: 0,
      });
      onImported(result);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to import those messages.');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setCsvText('');
    setRows([]);
    setMapping({});
    setError('');
    setDelimiterChoice('auto');
    setCustomDelimiter('');
    setQuoted(true);
    setTypeValueMap({});
    onClose();
  };

  return (
    <Sheet open={open} onClose={handleClose} title="Import messages from CSV">
      <div className="flex flex-col gap-4">
        {rows.length === 0 ? (
          <>
            <Button variant="bordered" onClick={() => inputRef.current?.click()} loading={loading}>
              Choose CSV file
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
                if (inputRef.current) inputRef.current.value = '';
              }}
            />
          </>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm text-black/70 dark:text-white/70">
              <input
                type="checkbox"
                checked={hasHeaderRow}
                onChange={(e) => {
                  setHasHeaderRow(e.target.checked);
                  autoMapColumns(e.target.checked ? rows[0] : undefined);
                }}
              />
              First row is a header
            </label>
            <Select
              label="Delimiter"
              value={delimiterChoice}
              onChange={(e) => {
                const next = e.target.value as DelimiterChoice;
                setDelimiterChoice(next);
                if (next !== 'custom') handleFormatChange({ delimiter: resolveDelimiterParam(next, customDelimiter), quoted });
              }}
            >
              {DELIMITER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            {delimiterChoice === 'custom' && (
              <TextField
                label="Custom delimiter character"
                placeholder="e.g. :"
                value={customDelimiter}
                onChange={(e) => {
                  const v = e.target.value;
                  setCustomDelimiter(v);
                  if (v) handleFormatChange({ delimiter: v, quoted });
                }}
              />
            )}
            <label className="flex items-center gap-2 text-sm text-black/70 dark:text-white/70">
              <input
                type="checkbox"
                checked={quoted}
                onChange={(e) => {
                  const v = e.target.checked;
                  setQuoted(v);
                  handleFormatChange({ delimiter: resolveDelimiterParam(delimiterChoice, customDelimiter), quoted: v });
                }}
              />
              Quoted fields (RFC 4180)
            </label>
            <div className="flex flex-col gap-3">
              {CSV_MESSAGE_FIELDS.map((field) => (
                <Select
                  key={field.key}
                  label={field.label + (field.required ? ' (required)' : '')}
                  value={mapping[field.key] ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMapping((m) => {
                      const next = { ...m };
                      if (v === '') delete next[field.key];
                      else next[field.key] = Number(v);
                      return next;
                    });
                  }}
                >
                  <option value="">None</option>
                  {Array.from({ length: columnCount }, (_, i) => (
                    <option key={i} value={i}>
                      {columnLabel(i)}
                    </option>
                  ))}
                </Select>
              ))}
            </div>
            {distinctTypeValues.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">Map Type values to signals</span>
                <span className="text-xs text-black/50 dark:text-white/50">
                  Leave "as-is" to import a value unchanged.
                </span>
                <div className="flex flex-col gap-3 max-h-64 overflow-y-auto">
                  {distinctTypeValues.map((v) => {
                    const selected = typeSelectValue(v);
                    return (
                      <div key={v} className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-black/70 dark:text-white/70 w-1/2 truncate shrink-0" title={v}>
                            {v}
                          </span>
                          <span className="text-black/30 dark:text-white/30 shrink-0">&rarr;</span>
                          <Select
                            aria-label={`Map "${v}" to`}
                            value={selected}
                            onChange={(e) => {
                              const next = e.target.value;
                              setTypeValueMap((m) => ({
                                ...m,
                                // Switching into Custom keeps whatever
                                // free text was already there (if any),
                                // rather than clobbering it back to blank.
                                [v]: next === CSV_TYPE_MAP_CUSTOM ? (signalNames.has(m[v] ?? '') ? '' : m[v] ?? '') : next,
                              }));
                            }}
                          >
                            <option value="">Leave as-is</option>
                            {dedupedSignals.map((s) => (
                              <option key={s.name} value={s.name}>
                                {s.emoji} {s.name}
                              </option>
                            ))}
                            <option value={CSV_TYPE_MAP_CUSTOM}>Custom…</option>
                          </Select>
                        </div>
                        {selected === CSV_TYPE_MAP_CUSTOM && (
                          <TextField
                            aria-label={`Custom type for "${v}"`}
                            placeholder={v}
                            value={typeValueMap[v] ?? ''}
                            onChange={(e) => setTypeValueMap((m) => ({ ...m, [v]: e.target.value }))}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">
                {previewRows.length} message{previewRows.length === 1 ? '' : 's'} will be imported
              </span>
              <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto">
                {previewRows.slice(0, 5).map((row, i) => (
                  <div key={i} className="text-sm text-black/70 dark:text-white/70 truncate">
                    <span className="font-medium">{fieldValue(row, 'sender') ?? 'you'}:</span> {fieldValue(row, 'text')}
                  </div>
                ))}
              </div>
              {previewRows.length > 5 && (
                <span className="text-xs text-black/50 dark:text-white/50">+ {previewRows.length - 5} more</span>
              )}
              {dataRows.length > previewRows.length && (
                <span className="text-xs text-black/50 dark:text-white/50">
                  {dataRows.length - previewRows.length} row(s) will be skipped - no Text column mapped or empty in that row.
                </span>
              )}
            </div>
            <Button onClick={commitImport} disabled={previewRows.length === 0} loading={importing}>
              Import
            </Button>
          </>
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    </Sheet>
  );
}
